import { ApiService } from "../../../../shared/services/api/api.service";

import { Component, ElementRef, ViewChild, OnInit } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { HttpClient } from "@angular/common/http";

import { CommonModule, NgFor, NgIf } from "@angular/common";

import { MatDialog } from "@angular/material/dialog";

import { GloTagComponent } from "../../../../shared/components/tag/tag.component";

import {
  GloTabContentDirective,
  GloTabsComponent,
} from "../../../../shared/components/tabs/tabs.component";

import { GoogleMapComponent } from "../../shared/google-map/google-map.component";

import { ServiceAppointmentComponent } from "../../../customer/appointments/service-appointment/service-appointment.component";

import {
  Service,
  ServicesResponse,
} from "../../../../shared/models/service.model";

import {
  ReviewApiResponse,
  Salon,
  SalonReview,
} from "../../../../shared/models/salon.model";

import { GalleryViewModalComponent } from "../../shared/gallery/gallery/gallery-view-modal.component";

import { LoaderComponent } from "../../../../shared/components/loader/loader.component";

import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";

import { EmptyComponent } from "../../../../shared/components/empty/empty.component";

import { environment } from "../../../../../environments/environment";

@Component({
  selector: "customer-salon-details",

  imports: [
    CommonModule,
    NgFor,
    NgIf,

    GloTagComponent,

    GloTabsComponent,
    GloTabContentDirective,

    GoogleMapComponent,

    ServiceAppointmentComponent,

    LoaderComponent,

    EmptyComponent,
  ],

  templateUrl: "./salon-details.component.html",

  styleUrl: "./salon-details.component.scss",
})
export class CustomerSalonDetailsComponent implements OnInit {
  @ViewChild("serviceTabContent")
  serviceTabContent?: ElementRef<HTMLDivElement>;

  @ViewChild(GloTabsComponent)
  GloTabsComponent?: GloTabsComponent;

  apiUrl = environment.apiUrl;

  assetUrl = environment.assetBaseUrl;

  /* =========================================================
     Tabs
  ========================================================== */

  tabs = [
    {
      key: "overview",
      label: "Overview",
    },
    {
      key: "services",
      label: "Services",
    },
    {
      key: "reviews",
      label: "Reviews",
    },
  ];

  activeTab = this.tabs[0].key;

  /* =========================================================
     Salon
  ========================================================== */

  salonId = "";

  salonDetails: Salon | null = null;

  latitude = 0;

  longitude = 0;

  /* =========================================================
     Services
  ========================================================== */

  services: Service[] = [];

  filteredServices: Service[] = [];

  servicesNames: string[] = [];

  activeServicesTab = "Featured";

  serviceCategories: string[] = ["Featured", "Hair", "Esthetics", "Nail"];

  servicePriceRange: {
    minPrice: number;
    maxPrice: number;
  } = {
    minPrice: 0,
    maxPrice: 0,
  };

  isLoadingServices = false;

  servicesError: string | null = null;

  /* =========================================================
     Pagination
  ========================================================== */

  currentPage = 1;

  totalCount = 0;

  hasNext = false;

  hasPrevious = false;

  pageSize = 10;

  /* =========================================================
     Reviews
  ========================================================== */

  salonReview: SalonReview[] = [];

  /* =========================================================
     Booking modal
  ========================================================== */

  showAppointmentModal = false;

  selectedService: Service | null = null;

  constructor(
    private readonly route: ActivatedRoute,

    private readonly http: HttpClient,

    private readonly dialog: MatDialog,

    private readonly ApiService: ApiService,
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.salonId = params["id"];

      if (!this.salonId) {
        return;
      }

      this.getSalonDetails(this.salonId);

      this.getSalonReview(this.salonId);
    });
  }

  /* =========================================================
     Salon
  ========================================================== */

  getSalonDetails(salonId: string): void {
    if (!salonId) {
      return;
    }

    this.isLoadingServices = true;

    this.servicesError = null;

    this.ApiService.request<Salon>(
      API_ENDPOINTS.public.salonCatalog(salonId),

      "GET",
    ).subscribe({
      next: (response: any) => {
        this.salonDetails = response;

        this.services = response.services ?? [];

        this.servicesNames = this.services
          .map((item) => item?.name)
          .filter(Boolean);

        this.servicePriceRange = this.getMinMaxPrices(this.services);

        this.extractServiceCategories();

        this.getFilteredServices(this.services);

        this.totalCount = this.services.length;

        this.isLoadingServices = false;
      },

      error: (error) => {
        console.error("Failed to fetch salon details:", error);

        this.servicesError = "Unable to load this salon right now.";

        this.isLoadingServices = false;
      },
    });
  }

  /* =========================================================
     Price range
  ========================================================== */

  getMinMaxPrices(list: any[]): {
    minPrice: number;
    maxPrice: number;
  } {
    if (!list || list.length === 0) {
      return {
        minPrice: 0,
        maxPrice: 0,
      };
    }

    const allPrices = list
      .map((item) => Number(item.price))
      .filter((price) => !Number.isNaN(price));

    if (allPrices.length === 0) {
      return {
        minPrice: 0,
        maxPrice: 0,
      };
    }

    return {
      minPrice: Math.min(...allPrices),

      maxPrice: Math.max(...allPrices),
    };
  }

  /* =========================================================
     Separate service pagination endpoint
  ========================================================== */

  loadServices(page = 1): void {
    if (!this.salonId) {
      return;
    }

    this.isLoadingServices = true;

    this.servicesError = null;

    this.ApiService.request<ServicesResponse>(
      API_ENDPOINTS.public.salonServicesPagination(this.salonId, page),

      "GET",
    ).subscribe({
      next: (response) => {
        this.services = response.results;

        this.totalCount = response.count;

        this.hasNext = !!response.next;

        this.hasPrevious = !!response.previous;

        this.currentPage = page;

        this.servicesNames = this.services
          .map((service) => service.name)
          .filter(Boolean);

        this.servicePriceRange = this.getMinMaxPrices(this.services);

        this.extractServiceCategories();

        this.getFilteredServices(this.services);

        this.isLoadingServices = false;
      },

      error: (error) => {
        console.error("Error loading services:", error);

        this.servicesError = "Failed to load services. Please try again.";

        this.isLoadingServices = false;
      },
    });
  }

  /* =========================================================
     Gallery
  ========================================================== */

  openGalleryModal(): void {
    this.dialog.open(GalleryViewModalComponent, {
      width: "900px",

      maxWidth: "94vw",

      height: "88vh",

      panelClass: "salon-gallery-dialog",
    });
  }

  /* =========================================================
     Service category filtering
  ========================================================== */

  private extractServiceCategories(): void {
    const categories = new Set<string>(["Featured"]);

    this.services.forEach((service) => {
      categories.add(this.categorizeService(service.name));
    });

    /*
     * Keep a predictable ordering while
     * only showing categories that actually
     * exist.
     */
    const preferredOrder = ["Featured", "Hair", "Esthetics", "Nail", "Other"];

    this.serviceCategories = preferredOrder.filter((category) =>
      categories.has(category),
    );

    if (!this.serviceCategories.includes(this.activeServicesTab)) {
      this.activeServicesTab = "Featured";
    }
  }

  private categorizeService(serviceName: string): string {
    const name = (serviceName || "").toLowerCase();

    if (
      name.includes("hair") ||
      name.includes("cut") ||
      name.includes("style") ||
      name.includes("colour") ||
      name.includes("color") ||
      name.includes("blonde") ||
      name.includes("blowout") ||
      name.includes("blow dry")
    ) {
      return "Hair";
    }

    if (
      name.includes("facial") ||
      name.includes("face") ||
      name.includes("skin") ||
      name.includes("wax") ||
      name.includes("brow") ||
      name.includes("lash")
    ) {
      return "Esthetics";
    }

    if (
      name.includes("nail") ||
      name.includes("manicure") ||
      name.includes("pedicure") ||
      name.includes("gel")
    ) {
      return "Nail";
    }

    return "Other";
  }

  onTabChange(activeTab: string): void {
    this.activeServicesTab = activeTab;

    this.getFilteredServices(this.services);
  }

  getFilteredServices(services: Service[]): void {
    if (this.activeServicesTab === "Featured") {
      this.filteredServices = [...services];

      return;
    }

    this.filteredServices = services.filter(
      (service) =>
        this.categorizeService(service.name) === this.activeServicesTab,
    );
  }

  /* =========================================================
     Service pagination
  ========================================================== */

  onNextPage(): void {
    if (this.hasNext) {
      this.loadServices(this.currentPage + 1);
    }
  }

  onPreviousPage(): void {
    if (this.hasPrevious) {
      this.loadServices(this.currentPage - 1);
    }
  }

  /* =========================================================
     Booking
  ========================================================== */

  scrollIntoView(): void {
    /*
     * Change tab first. Angular may not have rendered
     * the service-tab template until after this call.
     */
    this.GloTabsComponent?.setActiveTab("services");

    window.setTimeout(() => {
      this.serviceTabContent?.nativeElement.scrollIntoView({
        behavior: "smooth",

        block: "start",

        inline: "nearest",
      });
    }, 80);
  }

  onAddService(service: Service): void {
    this.selectedService = service;

    this.showAppointmentModal = true;
  }

  onCloseModal(): void {
    this.showAppointmentModal = false;

    this.selectedService = null;
  }

  onAppointmentBooked(appointmentData: any): void {
    console.log("Appointment booked successfully:", appointmentData);

    this.selectedService = null;
  }

  /* =========================================================
     Reviews
  ========================================================== */

  getSalonReview(id: string): void {
    this.ApiService.request<ReviewApiResponse>(
      API_ENDPOINTS.review.getCustomerReview(id),

      "GET",
    ).subscribe({
      next: (response) => {
        this.salonReview = response.results;
      },

      error: (error) => {
        console.error("Unable to load salon reviews:", error);
      },
    });
  }

  /* =========================================================
     Formatting
  ========================================================== */

  formatPrice(price: string): string {
    const parsed = Number.parseFloat(price);

    if (Number.isNaN(parsed)) {
      return "$0.00";
    }

    return `$${parsed.toFixed(2)}`;
  }

  trackServiceById(
    index: number,

    item: any,
  ): string | number {
    return item?.service_id || item?.id || index;
  }
}
